/**
 * All Courses — Client-Side Filtering
 *
 * Pure vanilla JS. No Algolia. No jQuery dependency for filtering.
 * Reads course data from a JSON blob rendered server-side and applies
 * filters in-memory, toggling visibility of pre-rendered DOM cards.
 *
 * @since 2026-03-30
 * @see GitHub #412
 */

import "./sass/all-courses.scss";
import {
  DEFAULT_COURSE_SORT,
  getCourseSortFromUrl,
  getSortedCourseIndices,
  setCourseSortInUrl,
} from "./course-sort.mjs";

document.addEventListener("DOMContentLoaded", () => {
  // ── Data ──────────────────────────────────────────────────────────
  const dataEl = document.getElementById("ac-course-data");
  const stateEl = document.getElementById("ac-initial-state");
  if (!dataEl) return;

  const entityDecoder = document.createElement("textarea");
  const decodeHtmlEntities = (value) => {
    if (typeof value !== "string" || !value.includes("&")) {
      return value;
    }

    entityDecoder.innerHTML = value;
    return entityDecoder.value;
  };

  const normalizeCourse = (course) => ({
    ...course,
    title: decodeHtmlEntities(course.title),
    department: decodeHtmlEntities(course.department),
    audience: Array.isArray(course.audience)
      ? course.audience.map((value) => decodeHtmlEntities(value))
      : decodeHtmlEntities(course.audience),
    day: Array.isArray(course.day)
      ? course.day.map((value) => decodeHtmlEntities(value))
      : course.day,
    format: Array.isArray(course.format)
      ? course.format.map((value) => decodeHtmlEntities(value))
      : course.format,
    level: decodeHtmlEntities(course.level),
    time_of_day: Array.isArray(course.time_of_day)
      ? course.time_of_day.map((value) => decodeHtmlEntities(value))
      : course.time_of_day,
  });

  const courses = JSON.parse(dataEl.textContent).map(normalizeCourse);
  const initialState = stateEl ? JSON.parse(stateEl.textContent) : {};
  const cards = document.querySelectorAll(".ac-course-card");

  const encodedDepartments = {
    "bike-shop": "Bike Shop",
    blacksmithing: "Blacksmithing",
    ceramics: "Ceramics",
    enameling: "Enameling",
    "fire-performance": "Fire & Performance",
    foundry: "Foundry",
    "glass-blowing": "Glass Blowing",
    "glass-casting-coldworking": "Glass Casting & Coldworking",
    "glass-flameworking": "Glass Flameworking",
    "glass-fusing-slumping": "Glass Fusing & Slumping",
    jewelry: "Jewelry",
    "kinetics-electronics": "Kinetics & Electronics",
    leather: "Leather",
    "leather-textiles-fine-arts": "Leather, Textiles, & Fine Arts",
    "machine-shop": "Machine Shop",
    moldmaking: "Moldmaking",
    "neon-light": "Neon & Light",
    stoneworking: "Stoneworking",
    welding: "Welding",
    woodworking: "Woodworking",
    "youth-after-care": "Youth After Care",
  };

  const encodedAudiences = {
    family: "Family",
    "ages-16-plus": "Ages 16+",
    "ages-18-plus": "Ages 18+",
    "ages-12-18": "Ages 12-18",
    "ages-14-18": "Ages 14-18",
    "ages-8-11": "Ages 8-11",
    "all-ages": "All Ages",
  };
  const showingEl = document.getElementById("ac-showing");
  const noResultsEl = document.getElementById("ac-no-results");

  // ── Lazy-load images via IntersectionObserver ─────────────────────
  const lazyImages = document.querySelectorAll("img.ac-card-image[data-src]");
  if ("IntersectionObserver" in window) {
    const imgObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
            imgObserver.unobserve(img);
          }
        });
      },
      { rootMargin: "200px" }
    );

    lazyImages.forEach((img) => imgObserver.observe(img));
  } else {
    // Fallback: load all images immediately.
    lazyImages.forEach((img) => {
      img.src = img.dataset.src;
      img.removeAttribute("data-src");
    });
  }

  // ── Filter state ──────────────────────────────────────────────────
  const state = {
    query: "",
    hideOOS: false,
    departments: [],
    audiences: [],
    days: [],
    times: [],
    levels: [],
    priceMin: 0,
    priceMax: 2000,
    dateStart: null,
    dateEnd: null,
    spotsMin: 0,
    spotsMax: 20,
    sort: DEFAULT_COURSE_SORT,
  };

  // ── DOM refs ──────────────────────────────────────────────────────
  const searchInput = document.getElementById("ac-search");
  const hideOOSInput = document.getElementById("ac-hide-oos");
  const priceMinInput = document.getElementById("ac-price-min");
  const priceMaxInput = document.getElementById("ac-price-max");
  const dateStartInput = document.getElementById("ac-date-start");
  const dateEndInput = document.getElementById("ac-date-end");
  const sortSelect = document.getElementById("ac-sort");
  const clearBtn = document.getElementById("ac-clear-filters");
  const toggleBtn = document.getElementById("ac-toggle-sidebar");
  const sidebar = document.getElementById("ac-sidebar");
  const spotsMinInput = document.getElementById("ac-spots-min");
  const spotsMaxInput = document.getElementById("ac-spots-max");

  // ── Helpers ───────────────────────────────────────────────────────
  function getCheckedValues(name) {
    return Array.from(
      document.querySelectorAll(`input[name="${name}"]:checked`)
    ).map((el) => el.value);
  }

  function normalizeDepartmentLabel(value) {
    return (value || "")
      .replace(/\+/g, " ")
      .replace(/&amp;/gi, "&")
      .replace(/\s+/g, " ")
      .trim();
  }

  function getDepartmentName(slugOrLabel) {
    const normalizedInput = normalizeDepartmentLabel(
      decodeURIComponent((slugOrLabel || "").replace(/^\/+|\/+$/g, ""))
    );

    if (!normalizedInput) {
      return null;
    }

    if (encodedDepartments[normalizedInput]) {
      return encodedDepartments[normalizedInput];
    }

    const matchingDepartment = Object.values(encodedDepartments).find(
      (department) => normalizeDepartmentLabel(department) === normalizedInput
    );

    return matchingDepartment || normalizedInput;
  }

  function getAudienceNames(slugs) {
    return slugs.map((slug) => encodedAudiences[slug] || null).filter(Boolean);
  }

  function getUrlDrivenState() {
    const url = new URL(window.location.href);
    const pathMatch = url.pathname.match(
      /(?:course-search|all-courses)\/(.*?)\/?$/
    );
    const pathSlug = pathMatch && pathMatch[1] ? pathMatch[1] : "";

    let department = initialState.department || null;
    let audience = Array.isArray(initialState.audience)
      ? [...initialState.audience]
      : null;

    if (pathSlug === "adult") {
      department = null;
      audience = ["Ages 18+", "Ages 16+", "Family"];
    } else if (pathSlug === "youth") {
      department = null;
      audience = ["Ages 8-11", "Ages 12-18", "Ages 14-18"];
    } else {
      const mappedDepartment = getDepartmentName(pathSlug);
      if (mappedDepartment) {
        department = mappedDepartment;
      }
    }

    const queryDepartment = url.searchParams.get("fdepartment");
    if (queryDepartment) {
      const mappedDepartment = getDepartmentName(queryDepartment);
      if (mappedDepartment) {
        department = mappedDepartment;
      }
    }

    const queryAudience = url.searchParams.get("faudience");
    if (queryAudience) {
      const mappedAudiences = getAudienceNames(
        queryAudience.split(",").map((value) => value.trim())
      );
      if (mappedAudiences.length) {
        audience = mappedAudiences;
      }
    }

    return {
      department,
      audience,
      query: url.searchParams.get("query") || "",
      sort: getCourseSortFromUrl(url),
    };
  }

  function matchesQuery(course) {
    if (!state.query) return true;
    const q = state.query.toLowerCase();
    return (
      course.title.toLowerCase().includes(q) ||
      (course.department && course.department.toLowerCase().includes(q)) ||
      (course.audience && course.audience.toLowerCase().includes(q))
    );
  }

  function matchesCheckboxFilter(courseVal, filterArr) {
    if (!filterArr.length) return true;
    if (Array.isArray(courseVal)) {
      return courseVal.some((v) => filterArr.includes(v));
    }
    return filterArr.includes(courseVal);
  }

  function matchesPrice(course) {
    return course.price >= state.priceMin && course.price <= state.priceMax;
  }

  function matchesDateRange(course) {
    if (!state.dateStart && !state.dateEnd) return true;
    const start = state.dateStart ? state.dateStart / 1000 : 0;
    const end = state.dateEnd ? state.dateEnd / 1000 : Infinity;
    return course.start_dates.some((d) => d >= start && d <= end);
  }

  function matchesSpots(course) {
    if (!Array.isArray(course.stocks) || !course.stocks.length) return false;
    return course.stocks.some(
      (spots) => spots >= state.spotsMin && spots <= state.spotsMax
    );
  }

  function courseVisible(course) {
    if (state.hideOOS && !course.is_in_stock) return false;
    if (!matchesQuery(course)) return false;
    if (!matchesCheckboxFilter(course.department, state.departments))
      return false;
    if (!matchesCheckboxFilter(course.audience, state.audiences)) return false;
    if (!matchesCheckboxFilter(course.day, state.days)) return false;
    if (!matchesCheckboxFilter(course.time_of_day, state.times)) return false;
    if (!matchesCheckboxFilter(course.level, state.levels)) return false;
    if (!matchesPrice(course)) return false;
    if (!matchesDateRange(course)) return false;
    if (!matchesSpots(course)) return false;
    return true;
  }

  // ── Sort ──────────────────────────────────────────────────────────
  function getSortedIndices() {
    return getSortedCourseIndices(courses, state.sort);
  }

  // ── Render ────────────────────────────────────────────────────────
  let filterTimeout;
  function applyFilters() {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
      const sortedIndices = getSortedIndices();
      let visible = 0;

      // Reorder DOM and toggle visibility.
      sortedIndices.forEach((idx, order) => {
        const card = cards[idx];
        const show = courseVisible(courses[idx]);
        card.style.display = show ? "" : "none";
        card.style.order = order;
        if (show) visible++;
      });

      showingEl.textContent = visible;
      noResultsEl.style.display = visible === 0 ? "" : "none";

      // Update department counts.
      document.querySelectorAll(".ac-count[data-dept]").forEach((el) => {
        const dept = el.dataset.dept;
        const count = courses.filter(
          (c) => c.department === dept && courseVisible(c)
        ).length;
        el.textContent = `(${count})`;
      });

      // Update audience counts.
      document.querySelectorAll(".ac-count[data-audience]").forEach((el) => {
        const audience = el.dataset.audience;
        const count = courses.filter(
          (c) => c.audience === audience && courseVisible(c)
        ).length;
        el.textContent = `(${count})`;
      });
    }, 50);
  }

  // ── Event listeners ───────────────────────────────────────────────
  searchInput.addEventListener("input", () => {
    state.query = searchInput.value;
    applyFilters();
  });

  hideOOSInput.addEventListener("change", () => {
    state.hideOOS = hideOOSInput.checked;
    applyFilters();
  });

  ["department", "audience", "day", "time_of_day", "level"].forEach((name) => {
    document.querySelectorAll(`input[name="${name}"]`).forEach((el) => {
      el.addEventListener("change", () => {
        const key = {
          department: "departments",
          audience: "audiences",
          day: "days",
          time_of_day: "times",
          level: "levels",
        }[name];
        state[key] = getCheckedValues(name);
        applyFilters();
      });
    });
  });

  // ── Range Sliders ──────────────────────────────────────────────────
  function initSlider(
    trackEl,
    minInput,
    maxInput,
    dataMin,
    dataMax,
    formatter,
    onChange
  ) {
    if (!trackEl) return;

    const progressEl = trackEl.querySelector(".ac-slider-progress");
    const handleMin = trackEl.querySelector('[data-role="min"]');
    const handleMax = trackEl.querySelector('[data-role="max"]');
    const labels = trackEl.parentElement.querySelector(".ac-slider-labels");
    const labelMin = labels
      ? labels.querySelector(".ac-slider-label-min")
      : null;
    const labelMax = labels
      ? labels.querySelector(".ac-slider-label-max")
      : null;

    let currentMin = dataMin;
    let currentMax = dataMax;
    const range = dataMax - dataMin;

    function updateUI() {
      const pctMin = ((currentMin - dataMin) / range) * 100;
      const pctMax = ((currentMax - dataMin) / range) * 100;

      handleMin.style.left = pctMin + "%";
      handleMax.style.left = pctMax + "%";
      progressEl.style.left = pctMin + "%";
      progressEl.style.width = pctMax - pctMin + "%";

      if (labelMin) labelMin.textContent = formatter(currentMin);
      if (labelMax) labelMax.textContent = formatter(currentMax);

      minInput.value = currentMin;
      maxInput.value = currentMax;
    }

    function handleDrag(handleEl, isMin) {
      let dragging = false;

      function onMove(e) {
        if (!dragging) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const rect = trackEl.getBoundingClientRect();
        let pct = (clientX - rect.left) / rect.width;
        pct = Math.max(0, Math.min(1, pct));
        let val = Math.round(dataMin + pct * range);

        if (isMin) {
          val = Math.min(val, currentMax);
          currentMin = val;
        } else {
          val = Math.max(val, currentMin);
          currentMax = val;
        }
        updateUI();
      }

      function onUp() {
        dragging = false;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onUp);
        if (typeof onChange === "function") {
          onChange({
            min: parseFloat(minInput.value) || dataMin,
            max: parseFloat(maxInput.value) || dataMax,
          });
        }
        applyFilters();
      }

      handleEl.addEventListener("mousedown", (e) => {
        e.preventDefault();
        dragging = true;
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      });
      handleEl.addEventListener(
        "touchstart",
        () => {
          dragging = true;
          document.addEventListener("touchmove", onMove, { passive: true });
          document.addEventListener("touchend", onUp);
        },
        { passive: true }
      );
    }

    handleDrag(handleMin, true);
    handleDrag(handleMax, false);
    updateUI();

    return {
      reset(min = dataMin, max = dataMax) {
        currentMin = min;
        currentMax = max;
        updateUI();
      },
    };
  }

  // Initialize price slider
  const priceTrack = document.querySelector('[data-slider="price"]');
  const priceSlider = initSlider(
    priceTrack,
    priceMinInput,
    priceMaxInput,
    0,
    2000,
    (v) => "$" + v.toLocaleString(),
    ({ min, max }) => {
      state.priceMin = min;
      state.priceMax = max;
    }
  );

  // Initialize spots-left slider
  const spotsTrack = document.querySelector('[data-slider="spots"]');
  const spotsSlider =
    spotsTrack && spotsMinInput && spotsMaxInput
      ? initSlider(
          spotsTrack,
          spotsMinInput,
          spotsMaxInput,
          0,
          20,
          (v) => String(v),
          ({ min, max }) => {
            state.spotsMin = min;
            state.spotsMax = max;
          }
        )
      : null;

  dateStartInput.addEventListener("change", () => {
    state.dateStart = dateStartInput.value
      ? new Date(dateStartInput.value).getTime()
      : null;
    applyFilters();
  });
  dateEndInput.addEventListener("change", () => {
    state.dateEnd = dateEndInput.value
      ? new Date(dateEndInput.value).getTime()
      : null;
    applyFilters();
  });

  sortSelect.addEventListener("change", () => {
    state.sort = sortSelect.value;
    const nextUrl = setCourseSortInUrl(
      new URL(window.location.href),
      state.sort
    );
    window.history.pushState({ courseSort: state.sort }, "", nextUrl);
    applyFilters();
  });

  window.addEventListener("popstate", () => {
    state.sort = getCourseSortFromUrl(new URL(window.location.href));
    sortSelect.value = state.sort;
    applyFilters();
  });

  clearBtn.addEventListener("click", () => {
    searchInput.value = "";
    hideOOSInput.checked = false;
    priceMinInput.value = "0";
    priceMaxInput.value = "2000";
    dateStartInput.value = "";
    dateEndInput.value = "";

    document
      .querySelectorAll('.ac-checklist input[type="checkbox"]')
      .forEach((el) => {
        el.checked = false;
      });

    if (priceSlider) {
      priceSlider.reset(0, 2000);
    }

    if (spotsSlider) {
      spotsSlider.reset(0, 20);
    }

    Object.assign(state, {
      query: "",
      hideOOS: false,
      departments: [],
      audiences: [],
      days: [],
      times: [],
      levels: [],
      priceMin: 0,
      priceMax: 2000,
      dateStart: null,
      dateEnd: null,
      spotsMin: 0,
      spotsMax: 20,
    });

    applyFilters();
  });

  // Mobile sidebar toggle.
  toggleBtn.addEventListener("click", () => {
    const expanded = sidebar.classList.toggle("open");
    toggleBtn.textContent = expanded ? "Hide Filters" : "Show Filters";
    toggleBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
  });

  // ── Apply initial state from URL ──────────────────────────────────
  const urlDrivenState = getUrlDrivenState();

  if (urlDrivenState.department) {
    document.querySelectorAll('input[name="department"]').forEach((el) => {
      if (el.value === urlDrivenState.department) {
        el.checked = true;
        state.departments = [urlDrivenState.department];
      }
    });
  }
  if (urlDrivenState.audience && Array.isArray(urlDrivenState.audience)) {
    document.querySelectorAll('input[name="audience"]').forEach((el) => {
      if (urlDrivenState.audience.includes(el.value)) {
        el.checked = true;
      }
    });
    state.audiences = urlDrivenState.audience;
  }
  if (urlDrivenState.query) {
    searchInput.value = urlDrivenState.query;
    state.query = urlDrivenState.query;
  }
  state.sort = urlDrivenState.sort;
  sortSelect.value = state.sort;

  // CSS Grid supports the `order` property natively — no need to switch to
  // flexbox. Keeping `display: grid` preserves the 3-column layout defined
  // in the stylesheet while still allowing sort-order changes via `order`.

  // Initial render.
  applyFilters();
});
